import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faStore, 
  faCheckCircle, 
  faTimesCircle, 
  faUser, 
  faPhone, 
  faEnvelope, 
  faCalendarAlt,
  faMoneyBillWave,
  faTrashAlt,
  faRefresh,
  faExclamationTriangle,
  faSearch,
  faBuilding,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import Navbar from "./Navbar";
import Footer from "./Footer";

// API Base URL
const API_BASE_URL = 'https://royaleheritagefiesta.com/royaleheritagefiesta/api';

function StallCancellation() {
  const location = useLocation();
  const navigate = useNavigate();
  const event = location.state?.event;
  
  const [stalls, setStalls] = useState([]);
  const [eventDetails, setEventDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState({});
  const [selectedStalls, setSelectedStalls] = useState(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [stallToCancel, setStallToCancel] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [summary, setSummary] = useState({
    totalStalls: 0,
    bookedStalls: 0,
    availableStalls: 0,
    totalRevenue: 0
  });

  useEffect(() => {
    if (!event) {
      alert("No event data provided");
      navigate("/adminDashboard");
      return;
    }
    fetchEventAndStalls();
  }, [event]);

  const fetchEventAndStalls = async () => {
    setLoading(true);
    try {
      console.log("Fetching event details for:", event.eventId);
      
      // Fetch event details
      const eventRes = await axios.get(`${API_BASE_URL}/events.php?eventId=${event.eventId}`);
      setEventDetails(eventRes.data);
      
      // Fetch stall bookings
      const bookingsRes = await axios.get(`${API_BASE_URL}/stall-bookings.php?eventId=${event.eventId}`);
      console.log("Bookings response:", bookingsRes.data);
      
      const bookings = bookingsRes.data.success ? bookingsRes.data.bookings : [];
      
      // Get total stalls based on layout
      let totalStalls = 0;
      let bookedStallsMap = {};
      
      if (eventRes.data.isCustomLayout && eventRes.data.customLayoutData) {
        const layoutData = typeof eventRes.data.customLayoutData === 'string' 
          ? JSON.parse(eventRes.data.customLayoutData) 
          : eventRes.data.customLayoutData;
        totalStalls = (layoutData.stalls || []).length;
        
        // Mark booked stalls
        bookings.forEach(booking => {
          bookedStallsMap[booking.stallNumber] = booking;
        });
        
        const stallsData = (layoutData.stalls || []).map(stall => ({
          stallId: stall.number,
          stallNumber: `Stall ${stall.number}`,
          price: stall.price || eventRes.data.stallPrice || 8000,
          status: bookedStallsMap[stall.number] ? "BOOKED" : "AVAILABLE",
          customerName: bookedStallsMap[stall.number]?.customerName,
          customerPhone: bookedStallsMap[stall.number]?.customerPhone,
          customerEmail: bookedStallsMap[stall.number]?.customerEmail,
          bookingDate: bookedStallsMap[stall.number]?.bookingDate,
          bookingId: bookedStallsMap[stall.number]?.bookingId
        }));
        
        setStalls(stallsData);
        
        const bookedCount = stallsData.filter(s => s.status === "BOOKED").length;
        setSummary({
          totalStalls: totalStalls,
          bookedStalls: bookedCount,
          availableStalls: totalStalls - bookedCount,
          totalRevenue: stallsData
            .filter(s => s.status === "BOOKED")
            .reduce((sum, s) => sum + (s.price || 0), 0)
        });
        
      } else {
        // Predefined layout
        switch(eventRes.data.stallPattern) {
          case "full": totalStalls = 50; break;
          case "ushape": totalStalls = 50; break;
          case "twosides": totalStalls = 50; break;
          default: totalStalls = 50;
        }
        
        // Create booked stalls map
        const bookedMap = {};
        bookings.forEach(booking => {
          bookedMap[booking.stallNumber] = booking;
        });
        
        const stallsData = [];
        for (let i = 1; i <= totalStalls; i++) {
          const booked = bookedMap[i];
          stallsData.push({
            stallId: i,
            stallNumber: `Stall ${i}`,
            price: eventRes.data.stallPrice || 8000,
            status: booked ? "BOOKED" : "AVAILABLE",
            customerName: booked?.customerName,
            customerPhone: booked?.customerPhone,
            customerEmail: booked?.customerEmail,
            bookingDate: booked?.bookingDate,
            bookingId: booked?.bookingId
          });
        }
        
        setStalls(stallsData);
        
        const bookedCount = stallsData.filter(s => s.status === "BOOKED").length;
        setSummary({
          totalStalls: totalStalls,
          bookedStalls: bookedCount,
          availableStalls: totalStalls - bookedCount,
          totalRevenue: stallsData
            .filter(s => s.status === "BOOKED")
            .reduce((sum, s) => sum + (s.price || 0), 0)
        });
      }
      
    } catch (error) {
      console.error("Error fetching data:", error);
      alert("Error fetching stalls: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelStall = async (stallId, bookingId) => {
    setStallToCancel({ stallId, bookingId });
    setShowConfirmModal(true);
  };

  const confirmCancel = async () => {
    if (!stallToCancel) return;
    
    setCancelling(prev => ({ ...prev, [stallToCancel.stallId]: true }));
    
    try {
      let response;
      if (stallToCancel.bookingId) {
        response = await axios.delete(`${API_BASE_URL}/stall-bookings.php?bookingId=${stallToCancel.bookingId}`);
      } else {
        response = await axios.delete(`${API_BASE_URL}/stall-bookings.php?eventId=${event.eventId}&stallNumber=${stallToCancel.stallId}`);
      }
      
      if (response.data.success) {
        alert(`✅ Booking cancelled successfully!`);
        await fetchEventAndStalls();
        setSelectedStalls(new Set());
      } else {
        alert("Failed to cancel: " + (response.data.message || "Unknown error"));
      }
      
    } catch (error) {
      console.error("Error cancelling stall:", error);
      alert("Error cancelling booking: " + (error.response?.data?.error || error.message));
    } finally {
      setCancelling(prev => ({ ...prev, [stallToCancel.stallId]: false }));
      setShowConfirmModal(false);
      setStallToCancel(null);
    }
  };

  const handleCancelMultiple = async () => {
    if (selectedStalls.size === 0) {
      alert("Please select at least one stall to cancel");
      return;
    }
    
    if (!window.confirm(`Are you sure you want to cancel ${selectedStalls.size} booked stall(s)? This action cannot be undone.`)) return;
    
    const stallIds = Array.from(selectedStalls);
    
    try {
      let cancelled = 0;
      for (const stallId of stallIds) {
        const stall = stalls.find(s => s.stallId === stallId);
        if (stall && stall.bookingId) {
          await axios.delete(`${API_BASE_URL}/stall-bookings.php?bookingId=${stall.bookingId}`);
          cancelled++;
        }
      }
      
      alert(`✅ Successfully cancelled ${cancelled} booking(s)!`);
      await fetchEventAndStalls();
      setSelectedStalls(new Set());
      
    } catch (error) {
      console.error("Error cancelling multiple bookings:", error);
      alert("Error cancelling bookings: " + (error.response?.data?.error || error.message));
    }
  };

  const toggleSelectStall = (stallId, isBooked) => {
    if (!isBooked) return;
    setSelectedStalls(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stallId)) newSet.delete(stallId);
      else newSet.add(stallId);
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    const bookedStalls = stalls.filter(s => s.status === "BOOKED");
    if (selectedStalls.size === bookedStalls.length) {
      setSelectedStalls(new Set());
    } else {
      setSelectedStalls(new Set(bookedStalls.map(s => s.stallId)));
    }
  };

  const filteredStalls = stalls.filter(stall => {
    if (filter === "booked" && stall.status !== "BOOKED") return false;
    if (filter === "available" && stall.status !== "AVAILABLE") return false;
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        stall.stallNumber?.toLowerCase().includes(searchLower) ||
        stall.customerName?.toLowerCase().includes(searchLower) ||
        stall.customerPhone?.includes(searchTerm)
      );
    }
    return true;
  });

  return (
    <>
      <Navbar />
      <div className="stall-cancellation">
        
        {/* Header Section with Inline Styles */}
        <div style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          padding: '2rem',
          marginBottom: '2rem',
          borderRadius: '0 0 20px 20px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}>
          <button 
            onClick={() => navigate("/adminDashboard")}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '30px',
              color: '#fff',
              cursor: 'pointer',
              marginBottom: '1rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px'
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
          </button>
          
          <h1 style={{ color: '#fff', fontSize: 'clamp(1.2rem, 5vw, 1.8rem)', margin: '0 0 0.5rem 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FontAwesomeIcon icon={faStore} style={{ color: '#b8860b' }} />
            Manage Stall Bookings
          </h1>
          
          <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0 }}>
            {event?.eventName} - {event?.eventDate}
          </p>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', marginTop: '5px' }}>
            <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '5px' }} />
            {event?.place || 'Venue TBA'}
          </p>
        </div>

        {/* Summary Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          padding: '0 2rem',
          marginBottom: '2rem'
        }}>
          {[
            { title: 'Total Stalls', value: summary.totalStalls, icon: faStore, color: '#6c757d' },
            { title: 'Booked Stalls', value: summary.bookedStalls, icon: faCheckCircle, color: '#28a745' },
            { title: 'Available Stalls', value: summary.availableStalls, icon: faTimesCircle, color: '#17a2b8' },
            { title: 'Total Revenue', value: `₹${summary.totalRevenue.toLocaleString()}`, icon: faMoneyBillWave, color: '#ffc107' }
          ].map((card, idx) => (
            <div key={idx} style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
              border: '1px solid #e0e0e0'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                background: `${card.color}20`,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                color: card.color
              }}>
                <FontAwesomeIcon icon={card.icon} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '0.8rem', color: '#666' }}>{card.title}</h3>
                <p style={{ margin: 0, fontSize: '1.3rem', fontWeight: 'bold', color: '#333' }}>{card.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Controls Bar */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          padding: '0 2rem',
          marginBottom: '1.5rem',
          alignItems: 'center'
        }}>
          <div style={{
            flex: 1,
            display: 'flex',
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '0.5rem 1rem',
            alignItems: 'center',
            gap: '10px',
            minWidth: '200px'
          }}>
            <FontAwesomeIcon icon={faSearch} style={{ color: '#999' }} />
            <input
              type="text"
              placeholder="Search by stall number, customer name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                border: 'none',
                outline: 'none',
                flex: 1,
                fontSize: '14px'
              }}
            />
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {[
              { id: 'all', label: 'All Stalls' },
              { id: 'booked', label: 'Booked Only' },
              { id: 'available', label: 'Available Only' }
            ].map(btn => (
              <button
                key={btn.id}
                onClick={() => setFilter(btn.id)}
                style={{
                  padding: '0.5rem 1rem',
                  background: filter === btn.id ? '#b8860b' : '#f8f9fa',
                  color: filter === btn.id ? '#fff' : '#333',
                  border: '1px solid #ddd',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.3s'
                }}
              >
                {btn.label}
              </button>
            ))}
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {selectedStalls.size > 0 && (
              <button
                onClick={handleCancelMultiple}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px'
                }}
              >
                <FontAwesomeIcon icon={faTrashAlt} />
                Cancel Selected ({selectedStalls.size})
              </button>
            )}
            <button
              onClick={fetchEventAndStalls}
              disabled={loading}
              style={{
                padding: '0.5rem 1rem',
                background: '#6c757d',
                color: '#fff',
                border: 'none',
                borderRadius: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '13px',
                opacity: loading ? 0.7 : 1
              }}
            >
              <FontAwesomeIcon icon={faRefresh} spin={loading} />
              Refresh
            </button>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {stalls.filter(s => s.status === "BOOKED").length > 0 && (
          <div style={{
            padding: '0.5rem 2rem',
            background: '#f8f9fa',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selectedStalls.size === stalls.filter(s => s.status === "BOOKED").length}
                onChange={toggleSelectAll}
              />
              Select All Booked Stalls
            </label>
          </div>
        )}

        {/* Stalls Grid */}
        <div style={{ padding: '2rem' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div className="spinner" style={{
                width: '40px',
                height: '40px',
                border: '3px solid #e0e0e0',
                borderTopColor: '#b8860b',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 1rem'
              }}></div>
              <p>Loading stall blueprint...</p>
            </div>
          ) : filteredStalls.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', background: '#fff', borderRadius: '12px' }}>
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '3rem', color: '#ffc107', marginBottom: '1rem' }} />
              <h3>No Stalls Found</h3>
              <p>No stalls match your search or filter criteria</p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '1rem'
            }}>
              {filteredStalls.map((stall) => (
                <div key={stall.stallId} style={{
                  background: stall.status === "BOOKED" ? '#fff5f5' : '#f0fff4',
                  border: stall.status === "BOOKED" ? '1px solid #fecaca' : '1px solid #c6f6d5',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  transition: 'transform 0.2s'
                }}>
                  {stall.status === "BOOKED" && (
                    <div style={{ padding: '0.75rem', borderBottom: '1px solid #fecaca', background: '#fff' }}>
                      <input
                        type="checkbox"
                        checked={selectedStalls.has(stall.stallId)}
                        onChange={() => toggleSelectStall(stall.stallId, true)}
                      />
                    </div>
                  )}
                  
                  <div style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FontAwesomeIcon icon={faStore} style={{ color: '#b8860b' }} />
                        <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{stall.stallNumber}</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        color: stall.status === "BOOKED" ? '#dc3545' : '#28a745',
                        fontWeight: 'bold',
                        fontSize: '0.8rem'
                      }}>
                        <FontAwesomeIcon icon={stall.status === "BOOKED" ? faCheckCircle : faTimesCircle} />
                        <span>{stall.status}</span>
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: '0.5rem' }}>
                      <FontAwesomeIcon icon={faMoneyBillWave} style={{ marginRight: '8px', color: '#28a745' }} />
                      <span style={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#333' }}>₹{stall.price.toLocaleString()}</span>
                    </div>
                    
                    {stall.status === "BOOKED" && (
                      <>
                        <div style={{
                          background: '#f8f9fa',
                          padding: '0.75rem',
                          borderRadius: '8px',
                          marginTop: '0.5rem'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem' }}>
                            <FontAwesomeIcon icon={faUser} style={{ color: '#b8860b', width: '14px' }} />
                            <span style={{ fontSize: '0.85rem', color: '#333' }}><strong>Customer:</strong> {stall.customerName || 'N/A'}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem' }}>
                            <FontAwesomeIcon icon={faPhone} style={{ color: '#b8860b', width: '14px' }} />
                            <span style={{ fontSize: '0.85rem', color: '#333' }}><strong>Phone:</strong> {stall.customerPhone || 'N/A'}</span>
                          </div>
                          {stall.customerEmail && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem' }}>
                              <FontAwesomeIcon icon={faEnvelope} style={{ color: '#b8860b', width: '14px' }} />
                              <span style={{ fontSize: '0.85rem', color: '#333' }}><strong>Email:</strong> {stall.customerEmail}</span>
                            </div>
                          )}
                          {stall.bookingDate && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#b8860b', width: '14px' }} />
                              <span style={{ fontSize: '0.85rem', color: '#333' }}><strong>Booked on:</strong> {new Date(stall.bookingDate).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                        
                        <button
                          onClick={() => handleCancelStall(stall.stallId, stall.bookingId)}
                          disabled={cancelling[stall.stallId]}
                          style={{
                            width: '100%',
                            marginTop: '1rem',
                            padding: '0.6rem',
                            background: '#dc3545',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            fontSize: '0.85rem',
                            fontWeight: 'bold',
                            transition: 'all 0.3s',
                            opacity: cancelling[stall.stallId] ? 0.7 : 1
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#c82333'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#dc3545'}
                        >
                          {cancelling[stall.stallId] ? (
                            <>
                              <div className="small-spinner" style={{
                                width: '14px',
                                height: '14px',
                                border: '2px solid rgba(255,255,255,0.3)',
                                borderTopColor: '#fff',
                                borderRadius: '50%',
                                animation: 'spin 0.8s linear infinite'
                              }}></div>
                              Cancelling...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faTrashAlt} />
                              Cancel Booking
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(5px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '1rem'
          }}>
            <div style={{
              background: '#fff',
              borderRadius: '20px',
              maxWidth: '450px',
              width: '90%',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '1.5rem',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                position: 'relative'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  background: '#dc3545',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  color: '#fff'
                }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                </div>
                <h2 style={{ margin: 0, fontSize: '1.3rem' }}>Confirm Cancellation</h2>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#999'
                  }}
                >×</button>
              </div>
              <div style={{ padding: '1.5rem' }}>
                <p>Are you sure you want to cancel this stall booking?</p>
                <div style={{
                  background: '#fff3cd',
                  borderLeft: '4px solid #ffc107',
                  padding: '0.8rem',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginTop: '1rem'
                }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#856404' }} />
                  <span style={{ color: '#856404', fontSize: '0.85rem' }}>This action cannot be undone. The customer will lose this booking.</span>
                </div>
              </div>
              <div style={{
                padding: '1rem 1.5rem 1.5rem',
                display: 'flex',
                gap: '1rem',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  style={{
                    padding: '0.7rem 1.5rem',
                    background: '#6c757d',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  No, Go Back
                </button>
                <button
                  onClick={confirmCancel}
                  style={{
                    padding: '0.7rem 1.5rem',
                    background: '#dc3545',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Yes, Cancel Booking
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          
          .stall-cancellation {
            background: #f5f5f5;
            min-height: calc(100vh - 200px);
            border-left: 29.5px solid #000;
            border-right: 29.5px solid #000;
          }
          
          @media (max-width: 768px) {
            .stall-cancellation {
              border-left: 14px solid #000 !important;
              border-right: 14px solid #000 !important;
            }
          }
        `}</style>
      </div>
      <Footer />
    </>
  );
}

export default StallCancellation;